import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { AccessLinkController } from "../core/controllers/access-link.controller";
import { AccessLinkAuthResponse, AccessLinkDownload } from "../core/interfaces/access-link-auth-response";
import { CompanyUiCustomizationService } from "../core/lib/company-ui/company.ui-customization.service";
import { SnackbarService } from "../core/lib/snackbar.service";
import { environment } from "src/environments/environment";
import { CompanyUiSettings } from "../core/lib/company-ui/company.ui-settings";

/** Durata minima del loader in ms — evita il "glitch" grafico */
const MIN_LOADING_MS = 5000;

/** Unico MIME type accettato per l'upload */
const ACCEPTED_UPLOAD_MIME = "application/pdf";

@Component({
  selector: "app-access-link",
  templateUrl: "./access-link.component.html",
  styleUrls: ["./access-link.component.scss"]
})
export class AccessLinkComponent implements OnInit, OnDestroy {

  // ── Stato UI ────────────────────────────────────────────────────────────────

  /** Modale di caricamento iniziale visibile */
  isLoading: boolean = true;

  /** Messaggio di errore da mostrare nella modale (null = nessun errore) */
  loadingError: string | null = null;

  /** Modale timeout sessione visibile */
  sessionExpired: boolean = false;

  /** Modale di conferma invio visibile */
  showUploadConfirm: boolean = false;

  // ── Dati risposta API ───────────────────────────────────────────────────────

  authResponse: AccessLinkAuthResponse | null = null;

  // ── Branding — tutti i valori letti da environment, zero riferimenti hardcoded ─

  readonly backgroundImageStyle: string =
    `url('${environment.brand.theme.loginBackground}')`;

  readonly brandLogoSrc: string = environment.brand.theme.extendedLogo;

  readonly brandLogoOnDark: boolean =
    (environment.brand.theme as any).logoOnDark === true;

  companyLogoSrc: string | null = null;

  // ── Upload ──────────────────────────────────────────────────────────────────

  selectedFile: File | null = null;

  isUploading: boolean = false;

  // True mentre l'utente sta trascinando un file sopra la dropzone.
  // Pilota la classe modifier --drag-over per il feedback visivo.
  isDragOver: boolean = false;

  // ── Sessione ────────────────────────────────────────────────────────────────

  /** Minuti rimanenti (valore grezzo) */
  remainingMinutes: number = 0;

  /** Secondi rimanenti 0-59 (valore grezzo) */
  remainingSeconds: number = 0;

  private sessionIntervalId: ReturnType<typeof setInterval> | null = null;

  /**
   * Timestamp assoluto (ms) di scadenza della sessione.
   * Derivato direttamente dal datetime ISO restituito dall'API tramite
   * new Date(Session).getTime() — immune al throttling del browser nelle
   * tab in background: ad ogni tick calcoliamo (sessionExpiresAt - Date.now())
   * invece di decrementare un contatore relativo.
   */
  private sessionExpiresAt: number = 0;

  /** Token letto dal queryParam */
  private token: string = "";

  constructor(
    private activatedRoute: ActivatedRoute,
    private accessLinkController: AccessLinkController,
    private companyUiCustomizationService: CompanyUiCustomizationService,
    private snackbarService: SnackbarService
  ) {}

  // ── Lifecycle ────────────────────────────────────────────────────────────────

  async ngOnInit(): Promise<void> {

    this.activatedRoute.queryParams.subscribe(async (params) => {

      this.token = params["token"] ?? "";
      await this.initialize();

    });

  }

  ngOnDestroy(): void {

    this.clearSessionTimer();

  }

  // ── Getter display timer ──────────────────────────────────────────────────────

  get remainingMinutesFormatted(): string {
    return String(this.remainingMinutes).padStart(2, "0");
  }

  get remainingSecondsFormatted(): string {
    return String(this.remainingSeconds).padStart(2, "0");
  }

  get isTimerWarning(): boolean {
    return this.remainingMinutes < 2 && !this.sessionExpired;
  }

  // ── Inizializzazione ─────────────────────────────────────────────────────────

  /**
   * Carica i dati della pagina rispettando il delay minimo del loader.
   * Usato sia all'avvio che dopo ogni upload completato.
   */
  private async initialize(): Promise<void> {

    const minimumDelay = new Promise<void>((resolve) => setTimeout(resolve, MIN_LOADING_MS));

    try {

      const [authResponse] = await Promise.all([
        this.accessLinkController.auth(this.token),
        minimumDelay
      ]);

      this.authResponse = authResponse;
      await this.loadCompanyBranding(authResponse.CompanyCode);
      this.startSessionTimer(authResponse.Session);
      this.isLoading = false;

    } catch (err: any) {

      await minimumDelay;
      this.loadingError = "Link non valido o scaduto.";

    }

  }

  /**
   * Richiama initialize() dopo un upload completato.
   * Resetta lo stato UI in modo che il loader sia di nuovo visibile,
   * poi ricarica tutti i dati dal backend (downloads, upload flag, sessione).
   */
  private async reinitialize(): Promise<void> {

    // Fermo il timer corrente prima di ripartire
    this.clearSessionTimer();

    // Resetto lo stato UI: il loader tornerà visibile durante la nuova chiamata
    this.isLoading    = true;
    this.loadingError = null;
    this.authResponse = null;

    await this.initialize();

  }

  // ── Branding ─────────────────────────────────────────────────────────────────

  private async loadCompanyBranding(companyCode: string): Promise<void> {

    if (!companyCode) {

      this.companyLogoSrc = this.brandLogoSrc;
      return;

    }

    const company = await this.companyUiCustomizationService.getSettings(companyCode);
    const logo = await (company ?? new CompanyUiSettings(companyCode)).getLogo();

    this.companyLogoSrc = logo.default ? this.brandLogoSrc : logo.src;

  }

  // ── Download ──────────────────────────────────────────────────────────────────

  openDownload(download: AccessLinkDownload): void {

    if (!download.Endpoint) return;

    window.open(download.Endpoint, "_blank");

  }

  getFileIcon(type: string | null): string {

    if (!type) return "insert_drive_file";

    switch (type.toUpperCase()) {
      case "PDF":  return "picture_as_pdf";
      case "WORD": return "description";
      default:     return "insert_drive_file";
    }

  }

  getFileIconClass(type: string | null): string {

    if (!type) return "default";

    switch (type.toUpperCase()) {
      case "PDF":  return "pdf";
      case "WORD": return "word";
      default:     return "default";
    }

  }

  // ── Upload ────────────────────────────────────────────────────────────────────

  onFileSelected(event: Event): void {

    const input = event.target as HTMLInputElement;

    this.selectedFile = null;

    if (!input.files || input.files.length === 0) return;

    this.setSelectedFile(input.files[0], () => { input.value = ""; });

  }

  // ── Drag & Drop ─────────────────────────────────────────────────────────────────────
  // Tre handler standard per la dropzone. Il preventDefault() su dragover
  // è OBBLIGATORIO: senza, il browser non considera l'elemento un drop target
  // valido e il drop event non scatta. Mentre il drag è attivo la proprietà
  // isDragOver pilota il modifier visivo .al-upload-label--drag-over.

  onDragOver(event: DragEvent): void {

    event.preventDefault();
    event.stopPropagation();

    if (this.isUploading) return;

    this.isDragOver = true;

  }

  onDragLeave(event: DragEvent): void {

    event.preventDefault();
    event.stopPropagation();

    this.isDragOver = false;

  }

  onDrop(event: DragEvent): void {

    event.preventDefault();
    event.stopPropagation();

    this.isDragOver = false;

    // Difensivo: se il drop arriva mentre è in corso un upload lo ignoro
    // (il bottone è disabled ma la dropzone resterebbe attiva).
    if (this.isUploading) return;

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return;

    // Coerente con il flusso del file picker: prendo solo il primo file
    // (l'upload accetta un singolo file). La validazione MIME è nel helper.
    this.setSelectedFile(files[0]);

  }

  /**
   * Valida il MIME del file e, se accettato, lo assegna a selectedFile.
   * Centralizza la logica condivisa tra file picker e drag&drop.
   *
   * @param file - File candidato da assegnare
   * @param onInvalid - Callback opzionale invocata in caso di file rifiutato
   *                    (es. per resettare il valore dell'input nel picker)
   */
  private setSelectedFile(file: File, onInvalid?: () => void): void {

    if (file.type !== ACCEPTED_UPLOAD_MIME) {

      this.snackbarService.open("Formato non valido. È possibile caricare solo file PDF.");
      if (onInvalid) onInvalid();
      return;

    }

    this.selectedFile = file;

  }

  onUploadRequest(): void {

    if (!this.selectedFile || this.isUploading) return;

    this.showUploadConfirm = true;

  }

  async confirmUpload(): Promise<void> {

    this.showUploadConfirm = false;
    await this.executeUpload();

  }

  cancelUpload(): void {

    this.showUploadConfirm = false;

  }

  /**
   * Esegue l'upload e, in caso di successo, richiama reinitialize() per
   * aggiornare la pagina con i nuovi dati restituiti dal backend
   * (i download e l'Upload flag potrebbero cambiare dopo un invio).
   */
  private async executeUpload(): Promise<void> {

    if (!this.selectedFile) return;

    this.isUploading = true;

    try {

      await this.accessLinkController.uploadFile(this.selectedFile, this.token);

      this.selectedFile = null;

      const fileInput = document.getElementById("access-link-file-input") as HTMLInputElement | null;
      if (fileInput) fileInput.value = "";

      // Ricarica tutti i dati dal backend — il loader tornerà visibile
      // durante la chiamata e scomparirà quando i nuovi dati saranno pronti
      await this.reinitialize();

      // La snackbar di successo viene mostrata dopo che la pagina si è aggiornata
      this.snackbarService.open("File caricato con successo!");

    } catch (err) {

      this.snackbarService.open("Errore durante il caricamento del file. Riprova.");

    } finally {

      this.isUploading = false;

    }

  }

  // ── Sessione ──────────────────────────────────────────────────────────────────

  /**
   * Avvia il countdown della sessione a partire dal datetime ISO restituito dall'API.
   *
   * PATTERN TIMESTAMP ASSOLUTO — immune al throttling del browser:
   * Converte il datetime ISO in ms con new Date(sessionIso).getTime() e calcola
   * ad ogni tick (sessionExpiresAt - Date.now()). Quando la tab torna attiva
   * dopo essere stata in background il valore si riallinea immediatamente al
   * tempo reale senza ritardi accumulati.
   *
   * @param sessionIso - Datetime ISO 8601 di scadenza restituito dall'API (es. "2026-04-08T15:30:00Z")
   */
  private startSessionTimer(sessionIso: string): void {

    this.sessionExpiresAt = new Date(sessionIso).getTime();

    // Prima visualizzazione immediata prima del primo tick dell'interval
    this.tick();

    this.sessionIntervalId = setInterval(() => this.tick(), 1000);

  }

  /**
   * Calcola i secondi rimanenti dal timestamp assoluto e aggiorna la UI.
   * Se il tempo è scaduto ferma il timer e mostra la modale di timeout.
   */
  private tick(): void {

    const remainingMs = this.sessionExpiresAt - Date.now();

    if (remainingMs <= 0) {

      this.remainingMinutes = 0;
      this.remainingSeconds = 0;

      this.clearSessionTimer();
      this.sessionExpired = true;

      return;

    }

    const totalSeconds = Math.ceil(remainingMs / 1000);

    this.remainingMinutes = Math.floor(totalSeconds / 60);
    this.remainingSeconds = totalSeconds % 60;

  }

  private clearSessionTimer(): void {

    if (this.sessionIntervalId !== null) {

      clearInterval(this.sessionIntervalId);
      this.sessionIntervalId = null;

    }

  }

}
